"use client";

import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createTRPCClient, httpBatchStreamLink } from "@trpc/client";
import {
  createTRPCContext,
  createTRPCOptionsProxy,
} from "@trpc/tanstack-react-query";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import { type ReactNode, Suspense } from "react";
import superjson from "superjson";

import { GoogleMapsProvider } from "@/components/GoogleMapsProvider";
import { ThemeProvider } from "@/components/ThemeProvider";
import { SpinnerFullPage } from "@/components/ui/spinner";
import { ListingEditorVisibilityProvider } from "@/lib/context/ListingEditorVisibilityContext";
import { Provider as TanstackQueryProvider } from "@/lib/hooks/generated/tanstack-query";
import { OrganizationProvider } from "@/lib/organizationContext";
import { PostHogProvider } from "@/lib/PostHogProvider";
import { type TRPCRouter } from "@/lib/trpc/router";

// Type for error objects that might have status codes
interface ErrorWithStatus extends Error {
  status?: number;
  response?: {
    status?: number;
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 1, // 1 minute
      gcTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error) => {
        console.debug(
          "queryClient.defaultOptions.queries.retry",
          failureCount,
          error,
        );

        // Don't retry 4xx and 5xx errors (client and server errors)
        const errorWithStatus = error as ErrorWithStatus;
        const status =
          errorWithStatus?.status || errorWithStatus?.response?.status;
        if (status && status >= 400) {
          console.error("Not retrying 4xx and 5xx errors", errorWithStatus);
          return false;
        }
        // Retry other errors up to 2 times
        return false; // failureCount < 2;
      },
      retryDelay: (attempt) =>
        Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000), // Exponential backoff with a max delay of 30 seconds
    },
    mutations: {
      retry: (failureCount, error) => {
        console.debug(
          "queryClient.defaultOptions.mutations.retry",
          failureCount,
          error,
        );
        // Don't retry 4xx and 5xx errors for mutations
        const errorWithStatus = error as ErrorWithStatus;
        const status =
          errorWithStatus?.status || errorWithStatus?.response?.status;
        if (status && status >= 400) {
          console.error("Not retrying 4xx and 5xx errors", errorWithStatus);
          return false;
        }
        // Retry other errors up to 2 times
        return failureCount < 2;
      },
      retryDelay: (attempt) =>
        Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000), // Exponential backoff with a max delay of 30 seconds
    },
    dehydrate: { serializeData: superjson.serialize },
    hydrate: { deserializeData: superjson.deserialize },
  },
});

// Persist the React Query cache for session longevity
if (typeof window !== "undefined") {
  const persister = createSyncStoragePersister({
    storage: window.localStorage,
    key: "apartmentdibs:react-query",
    throttleTime: 1000,
  });
  persistQueryClient({
    queryClient,
    persister,
    maxAge: 1000 * 60 * 60 * 24, // 24 hours
  });
}

// Use a relative url for the client, but an absolute url for the server
// TODO: This will need to be updated when we deploy to Vercel
function getUrl() {
  const base = (() => {
    if (typeof window !== "undefined") {
      return "";
    }
    return `http://localhost:${process.env.PORT ?? 3000}`;
  })();
  return `${base}/api/trpc`;
}

function getModelUrl() {
  const base = (() => {
    if (typeof window !== "undefined") {
      return "";
    }
    return `http://localhost:${process.env.PORT ?? 3000}`;
  })();
  return `${base}/api/model`;
}

// This is where they type-safety comes in
const trpcClient = createTRPCClient<TRPCRouter>({
  links: [
    httpBatchStreamLink({
      url: getUrl(),
      transformer: superjson,
    }),
  ],
});

const serverHelpers = createTRPCOptionsProxy({
  client: trpcClient,
  queryClient: queryClient,
});

export function getContext() {
  return { queryClient, trpc: serverHelpers };
}

export const { TRPCProvider, useTRPC, useTRPCClient } =
  createTRPCContext<TRPCRouter>();

export default function Providers({ children }: { children: ReactNode }) {
  return (
    <PostHogProvider>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <GoogleMapsProvider>
          <TanstackQueryProvider value={{ endpoint: getModelUrl() }}>
            <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
              <QueryClientProvider client={queryClient}>
                <NuqsAdapter>
                  <OrganizationProvider>
                    <ListingEditorVisibilityProvider>
                      <Suspense fallback={<SpinnerFullPage />}>
                        {children}
                      </Suspense>
                      <ReactQueryDevtools initialIsOpen={false} />
                    </ListingEditorVisibilityProvider>
                  </OrganizationProvider>
                </NuqsAdapter>
              </QueryClientProvider>
            </TRPCProvider>
          </TanstackQueryProvider>
        </GoogleMapsProvider>
      </ThemeProvider>
    </PostHogProvider>
  );
}
